import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "./api";
import { Logo } from "./ui";

const BACKEND = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const CHAT_TOKEN_STORAGE_KEY = (token) => `pawcita:report-chat:${token}`;

function readChatToken(publicToken) {
  try {
    return localStorage.getItem(CHAT_TOKEN_STORAGE_KEY(publicToken)) || "";
  } catch {
    return "";
  }
}

function writeChatToken(publicToken, value) {
  try {
    if (value) {
      localStorage.setItem(CHAT_TOKEN_STORAGE_KEY(publicToken), value);
    } else {
      localStorage.removeItem(CHAT_TOKEN_STORAGE_KEY(publicToken));
    }
  } catch {
    /* localStorage bloqueado o modo privado: seguimos funcionando en memoria */
  }
}

function assetUrl(value) {
  if (!value) return "/dog-placeholder.svg";
  if (/^https?:\/\//i.test(value)) return value;
  return `${BACKEND}${value}`;
}

function hasMedicalAlerts(medical) {
  if (!medical) return false;
  return Boolean(
    medical.allergies ||
    medical.medical_conditions ||
    medical.current_medications
  );
}

function Detail({ label, value }) {
  if (value === undefined || value === null || value === "") return null;

  return (
    <div className="rounded-2xl bg-slate-50 px-4 py-3">
      <p className="text-[11px] font-black uppercase tracking-[0.14em] text-slate-400">
        {label}
      </p>
      <p className="mt-1 text-sm font-extrabold text-[#0B2D4D]">
        {String(value)}
      </p>
    </div>
  );
}

function ChatBubble({ msg }) {
  const mine = msg.from === "finder";
  return (
    <div
      className={`flex ${mine ? "justify-end" : "justify-start"}`}
      data-testid={`pawcita-id-chat-msg-${msg.id}`}
    >
      <div
        className={
          mine
            ? "max-w-[80%] rounded-2xl rounded-br-md bg-[#FF5C5C] px-4 py-2 text-sm font-semibold text-white shadow"
            : "max-w-[80%] rounded-2xl rounded-bl-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-[#0B2D4D]"
        }
      >
        <p className="whitespace-pre-wrap leading-5">{msg.text}</p>
      </div>
    </div>
  );
}

function ReportChat({ publicToken, dogName, initialChatToken }) {
  const [chatToken, setChatToken] = useState(
    initialChatToken || readChatToken(publicToken) || ""
  );
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [warn, setWarn] = useState("");
  const scrollerRef = useRef(null);
  const invalidated = useRef(false);

  const persistToken = useCallback((value) => {
    setChatToken(value);
    writeChatToken(publicToken, value);
  }, [publicToken]);

  const pull = useCallback(async () => {
    if (!chatToken || invalidated.current) return;
    try {
      const data = await api.pawcitaId.getReportChat(chatToken);
      setMessages(data?.messages || []);
      setError("");
    } catch (err) {
      if (err.status === 401 || err.status === 403) {
        // Token inválido o chat cerrado. Limpiamos.
        invalidated.current = true;
        persistToken("");
        setError(err.message || "El chat ya no está disponible.");
      } else if (!messages.length) {
        setError(err.message || "No pudimos cargar el chat.");
      }
    }
  }, [chatToken, messages.length, persistToken]);

  useEffect(() => {
    if (!chatToken) return undefined;
    pull();
    const t = setInterval(pull, 5000);
    return () => clearInterval(t);
  }, [chatToken, pull]);

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages.length]);

  const submit = async (e) => {
    e.preventDefault();
    const value = text.trim();
    if (!value || !chatToken) return;
    setSending(true);
    setWarn("");
    try {
      await api.pawcitaId.sendReportChatMessage(chatToken, value);
      setText("");
      await pull();
    } catch (err) {
      // Errores de moderación del backend siempre vienen como 400 con un
      // detail que contiene "Por seguridad". Reemplazamos por una copia
      // clara y accionable, sin borrar lo que el usuario escribió.
      const isModeration =
        err?.status === 400 && /por seguridad/i.test(err?.message || "");
      setWarn(
        isModeration
          ? "Por seguridad, no puedes compartir teléfonos, correos, enlaces ni redes sociales. Continúa conversando dentro de PawCita."
          : err?.message || "No se pudo enviar el mensaje."
      );
    } finally {
      setSending(false);
    }
  };

  if (!chatToken) {
    return null;
  }

  return (
    <section
      className="mt-5 overflow-hidden rounded-[26px] border border-slate-100 bg-white shadow-sm"
      data-testid="pawcita-id-report-chat"
    >
      <header className="border-b border-slate-100 bg-[#0B2D4D] px-5 py-4 text-white">
        <p className="text-xs font-black uppercase tracking-[0.16em] text-emerald-200">
          Chat seguro
        </p>
        <h3 className="mt-1 font-display text-lg font-black">
          Habla con la familia de {dogName || "esta mascota"}
        </h3>
        <p className="mt-1 text-xs leading-5 text-white/80">
          Puedes comunicarte aquí sin compartir tu teléfono ni correo. PawCita
          bloquea automáticamente teléfonos, emails, enlaces y redes sociales.
        </p>
      </header>

      <div
        ref={scrollerRef}
        className="max-h-72 space-y-2 overflow-y-auto bg-[#FBFCFF] px-4 py-4"
        data-testid="pawcita-id-report-chat-scroller"
      >
        {messages.length === 0 ? (
          <p className="py-6 text-center text-xs font-semibold text-slate-400">
            Aún no hay mensajes. Escribe algo para iniciar la conversación.
          </p>
        ) : (
          messages.map((m) => <ChatBubble key={m.id} msg={m} />)
        )}
      </div>

      {error ? (
        <p
          className="border-t border-rose-100 bg-rose-50 px-4 py-2 text-center text-xs font-bold text-rose-600"
          data-testid="pawcita-id-report-chat-error"
        >
          {error}
        </p>
      ) : null}

      <form
        onSubmit={submit}
        className="flex items-end gap-2 border-t border-slate-100 bg-white p-3"
      >
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={1000}
          rows={2}
          placeholder="Escribe un mensaje seguro…"
          data-testid="pawcita-id-report-chat-input"
          className="flex-1 resize-none rounded-2xl border border-slate-200 bg-[#FBFCFF] px-4 py-2 text-sm text-[#11243D] outline-none focus:border-[#67D5B5]"
        />
        <button
          type="submit"
          disabled={sending || !text.trim()}
          data-testid="pawcita-id-report-chat-send"
          className="rounded-full bg-[#FF5C5C] px-5 py-3 text-sm font-black text-white shadow disabled:opacity-50"
        >
          {sending ? "Enviando…" : "Enviar"}
        </button>
      </form>

      {warn ? (
        <p
          className="border-t border-amber-100 bg-amber-50 px-4 py-2 text-center text-xs font-bold text-amber-700"
          data-testid="pawcita-id-report-chat-warn"
        >
          {warn}
        </p>
      ) : null}
    </section>
  );
}

export default function PublicPawcitaId({ token }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [reportOpen, setReportOpen] = useState(false);
  const [locationLabel, setLocationLabel] = useState("");
  const [note, setNote] = useState("");
  const [coords, setCoords] = useState(null);
  const [locating, setLocating] = useState(false);
  const [locationError, setLocationError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [sent, setSent] = useState(false);
  const [chatToken, setChatToken] = useState(() => readChatToken(token));

  useEffect(() => {
    let alive = true;

    setLoading(true);
    setLoadError("");

    api.pawcitaId
      .publicProfile(token)
      .then((data) => {
        if (alive) setProfile(data);
      })
      .catch((err) => {
        if (alive) {
          setLoadError(
            err.message || "Este ID PawCita no está disponible."
          );
        }
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [token]);

  const medicalItems = useMemo(() => {
    const m = profile?.medical_alerts || {};

    return [
      ["Alergias", m.allergies],
      ["Condiciones médicas", m.medical_conditions],
      ["Medicamentos actuales", m.current_medications],
    ].filter(([, value]) => Boolean(value));
  }, [profile]);

  const requestLocation = () => {
    setLocationError("");

    if (!navigator.geolocation) {
      setLocationError(
        "Este dispositivo no permite compartir ubicación desde el navegador."
      );
      return;
    }

    setLocating(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCoords({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
        setLocating(false);
      },
      () => {
        setCoords(null);
        setLocating(false);
        setLocationError(
          "No pudimos obtener tu ubicación. Puedes escribir el lugar manualmente."
        );
      },
      {
        enableHighAccuracy: false,
        timeout: 12000,
        maximumAge: 60000,
      }
    );
  };

  const sendReport = async (e) => {
    e.preventDefault();

    setSubmitError("");

    if (!locationLabel.trim() && !note.trim() && !coords) {
      setSubmitError(
        "Indica dónde viste a la mascota, comparte tu ubicación o escribe un mensaje."
      );
      return;
    }

    setSubmitting(true);

    try {
      const res = await api.pawcitaId.reportFound(token, {
        location_label: locationLabel.trim(),
        note: note.trim(),
        lat: coords?.lat ?? null,
        lng: coords?.lng ?? null,
      });

      if (res?.chat_token) {
        writeChatToken(token, res.chat_token);
        setChatToken(res.chat_token);
      }

      setSent(true);
    } catch (err) {
      setSubmitError(
        err.message || "No pudimos enviar la información. Intenta nuevamente."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <main className="min-h-screen bg-[#FBFCFF] px-4 py-10">
        <div className="mx-auto flex min-h-[70vh] max-w-xl items-center justify-center">
          <div className="text-center">
            <div className="mx-auto h-11 w-11 animate-spin rounded-full border-4 border-rose-100 border-t-[#FF5C5C]" />
            <p className="mt-4 font-bold text-[#0B2D4D]">
              Cargando ID PawCita…
            </p>
          </div>
        </div>
      </main>
    );
  }

  if (!profile || loadError) {
    return (
      <main
        className="min-h-screen bg-[#FBFCFF] px-4 py-10"
        data-testid="pawcita-id-public-unavailable"
      >
        <div className="mx-auto max-w-lg rounded-[32px] border border-slate-100 bg-white p-8 text-center shadow-xl">
          <div className="text-5xl">🐾</div>
          <h1 className="mt-4 font-display text-2xl font-black text-[#0B2D4D]">
            ID PawCita no disponible
          </h1>
          <p className="mt-3 text-sm leading-6 text-slate-500">
            {loadError ||
              "La familia pudo haber desactivado temporalmente esta ficha."}
          </p>
          <a
            href="/"
            className="mt-6 inline-flex rounded-full bg-[#0B2D4D] px-6 py-3 text-sm font-black text-white"
          >
            Ir a PawCita
          </a>
        </div>
      </main>
    );
  }

  return (
    <main
      className="min-h-screen bg-gradient-to-b from-[#E4F7F0] via-[#FBFCFF] to-[#FFF8F1] px-4 pb-12 pt-6"
      data-testid="pawcita-id-public"
    >
      <div className="mx-auto max-w-xl">
        <header className="mb-5 text-center">
          <a
            href="/"
            className="inline-flex items-center gap-2 rounded-full bg-white/90 px-4 py-2 text-xs font-black tracking-[0.15em] text-[#0B2D4D] shadow-sm"
          >
            🐾 PAWCITA
          </a>

          <p className="mt-3 text-xs font-bold uppercase tracking-[0.18em] text-[#047857]">
            Identidad digital de mascota
          </p>
        </header>

        {profile.sos?.active ? (
          <section
            className="mb-4 rounded-[26px] border border-rose-200 bg-rose-50 p-5"
            data-testid="pawcita-id-public-sos"
          >
            <div className="flex items-start gap-3">
              <span className="text-3xl">🆘</span>

              <div>
                <p className="font-black text-rose-700">
                  {profile.name} está reportado como perdido
                </p>

                {profile.sos.last_seen_location_label ? (
                  <p className="mt-1 text-sm leading-5 text-rose-700">
                    Último lugar reportado:{" "}
                    <strong>{profile.sos.last_seen_location_label}</strong>
                  </p>
                ) : null}

                <p className="mt-2 text-xs font-semibold leading-5 text-rose-600">
                  Si tienes información, puedes enviarla directamente a su
                  familia desde esta página.
                </p>
              </div>
            </div>
          </section>
        ) : null}

        <section className="overflow-hidden rounded-[34px] border border-white bg-white shadow-[0_24px_70px_-28px_rgba(11,45,77,0.28)]">
          <div className="relative">
            <img
              src={assetUrl(profile.image)}
              alt={profile.name}
              className="h-72 w-full object-cover"
              style={{ objectPosition: "center top" }}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = "/dog-placeholder.svg";
              }}
            />

            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0B2D4D]/90 to-transparent px-6 pb-5 pt-16 text-white">
              <p className="text-xs font-black uppercase tracking-[0.16em] text-emerald-200">
                ID PawCita
              </p>
              <h1
                className="mt-1 font-display text-4xl font-black"
                data-testid="pawcita-id-public-name"
              >
                {profile.name}
              </h1>
            </div>
          </div>

          <div className="p-5 sm:p-6">
            <div className="grid grid-cols-2 gap-3">
              <Detail label="Raza" value={profile.breed} />
              <Detail
                label="Edad"
                value={
                  profile.age != null
                    ? `${profile.age} año${profile.age === 1 ? "" : "s"}`
                    : ""
                }
              />
              <Detail label="Sexo" value={profile.sex} />
              <Detail label="Color" value={profile.color} />
            </div>

            <div className="mt-5 rounded-3xl bg-[#E4F7F0] p-5">
              <p className="font-display text-lg font-black text-[#0B2D4D]">
                {profile.custom_message ||
                  `Hola, soy ${profile.name}. Si me encontraste, por favor avisa a mi familia.`}
              </p>
            </div>

            {hasMedicalAlerts(profile.medical_alerts) ? (
              <div
                className="mt-5 rounded-3xl border border-amber-200 bg-amber-50 p-5"
                data-testid="pawcita-id-medical-alerts"
              >
                <p className="font-black text-amber-900">
                  ⚕️ Información médica importante
                </p>

                <div className="mt-3 space-y-3">
                  {medicalItems.map(([label, value]) => (
                    <div key={label}>
                      <p className="text-xs font-black uppercase tracking-wide text-amber-700">
                        {label}
                      </p>
                      <p className="mt-1 text-sm font-semibold leading-5 text-amber-950">
                        {value}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {!sent ? (
              <>
                {!reportOpen ? (
                  <button
                    type="button"
                    data-testid="pawcita-id-found-open"
                    onClick={() => setReportOpen(true)}
                    className="mt-6 w-full rounded-full bg-[#FF5C5C] px-5 py-4 text-base font-black text-white shadow-[0_14px_30px_-12px_rgba(255,92,92,0.8)] transition active:translate-y-[1px]"
                  >
                    📍 Encontré a esta mascota
                  </button>
                ) : (
                  <form
                    onSubmit={sendReport}
                    className="mt-6 rounded-[28px] border border-slate-100 bg-slate-50 p-5"
                    data-testid="pawcita-id-found-form"
                  >
                    <h2 className="font-display text-xl font-black text-[#0B2D4D]">
                      Ayuda a {profile.name} a volver a casa
                    </h2>

                    <p className="mt-2 text-sm leading-5 text-slate-500">
                      Comparte únicamente la información que quieras enviar a
                      la familia.
                    </p>

                    <label className="mt-5 block text-sm font-extrabold text-[#0B2D4D]">
                      ¿Dónde lo viste o encontraste?
                    </label>

                    <input
                      type="text"
                      value={locationLabel}
                      maxLength={200}
                      onChange={(e) => setLocationLabel(e.target.value)}
                      placeholder="Ej: Parque Omar, cerca de la entrada principal"
                      data-testid="pawcita-id-found-location"
                      className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-[#11243D] outline-none focus:border-[#67D5B5]"
                    />

                    <button
                      type="button"
                      onClick={requestLocation}
                      disabled={locating}
                      data-testid="pawcita-id-found-geolocation"
                      className="mt-3 w-full rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-black text-emerald-700 disabled:opacity-50"
                    >
                      {locating
                        ? "Obteniendo ubicación…"
                        : coords
                        ? "✓ Ubicación compartida"
                        : "📍 Compartir mi ubicación actual"}
                    </button>

                    {locationError ? (
                      <p className="mt-2 text-xs font-semibold text-amber-700">
                        {locationError}
                      </p>
                    ) : null}

                    <label className="mt-5 block text-sm font-extrabold text-[#0B2D4D]">
                      Mensaje para la familia
                    </label>

                    <textarea
                      value={note}
                      maxLength={500}
                      rows={4}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder={`Ej: ${profile.name} está conmigo y se encuentra bien.`}
                      data-testid="pawcita-id-found-note"
                      className="mt-2 w-full resize-none rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-[#11243D] outline-none focus:border-[#67D5B5]"
                    />

                    {submitError ? (
                      <p
                        className="mt-3 text-sm font-bold text-rose-600"
                        data-testid="pawcita-id-found-error"
                      >
                        {submitError}
                      </p>
                    ) : null}

                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="pawcita-id-found-submit"
                      className="mt-5 w-full rounded-full bg-[#0B2D4D] px-5 py-4 text-sm font-black text-white disabled:opacity-50"
                    >
                      {submitting
                        ? "Enviando…"
                        : "Enviar información a la familia"}
                    </button>

                    <p className="mt-4 text-center text-[11px] leading-4 text-slate-400">
                      Tu ubicación solo se comparte si presionas el botón de
                      ubicación. PawCita no obtiene tu ubicación en segundo
                      plano.
                    </p>
                  </form>
                )}
              </>
            ) : (
              <div
                className="mt-6 rounded-[28px] border border-emerald-200 bg-emerald-50 p-6 text-center"
                data-testid="pawcita-id-found-success"
              >
                <div className="text-4xl">💚</div>

                <h2 className="mt-3 font-display text-xl font-black text-[#0B2D4D]">
                  Gracias por ayudar a {profile.name}
                </h2>

                <p className="mt-2 text-sm leading-6 text-emerald-800">
                  La familia recibió tu reporte a través de PawCita.
                </p>
              </div>
            )}

            {chatToken ? (
              <ReportChat
                publicToken={token}
                dogName={profile.name}
                initialChatToken={chatToken}
              />
            ) : null}

            <div className="mt-6 border-t border-slate-100 pt-5 text-center">
              <p className="text-xs font-semibold leading-5 text-slate-400">
                🔒 PawCita no muestra el teléfono, correo ni dirección de la
                familia en esta ficha.
              </p>
            </div>
          </div>
        </section>

        <footer className="py-7 text-center">
          <div className="flex justify-center">
            <Logo size="sm" />
          </div>
          <p className="mt-2 text-xs text-slate-400">
            Paws + Humans, Better Stories
          </p>
        </footer>
      </div>
    </main>
  );
}
